import { sendaForClass, type HeroClass } from '@somnhold/content';
import { sendaDef, sendaOf, type Hero, type HeroState } from '@somnhold/sim';
import { t } from '../i18n.ts';
import { h } from './dom.ts';
import { ICONS } from './icons.ts';

/**
 * A hero's name wherever it is shown (docs/SENDAS.md, «La etiqueta con el nombre»). A hero with its path's legacy
 * wears it on a light stone plate with a double dragon-red edge, its class's sigil on the left with an ember that
 * breathes (CSS only), and its title under the name in small capitals of the light red. Without a legacy it is the
 * name as it always was. One builder for the sheet, the party frames, the ally strip, the ranking and the report.
 */

export interface Legacy {
  cls: HeroClass;
  /** The title alone («de la Prueba»), without the name. */
  title: string;
}

/** Each class's sigil on the plate (the same the party frames show). */
export const SIGIL: Record<HeroClass, string> = { warrior: ICONS.sword, mage: ICONS.staff, ranger: ICONS.bow, artificer: ICONS.wrench, cleric: ICONS.sunStaff, barbarian: ICONS.axe, paladin: ICONS.mace, druid: ICONS.antlers };

/** The title of a class's path, without the name: the path's «{name}, de la …» with the name taken out. */
export const legacyTitle = (cls: HeroClass): string | null => {
  const def = sendaForClass(cls);
  if (!def) return null;
  const mark = '\u0000';
  const full = t(`senda.${def.id}.titled`, { name: mark });
  return full.replace(mark, '').replace(/^[\s,]+|[\s,]+$/g, '') || null;
};

/** A hero in the bastion: its path walked to its end (every chapter, the last one won). */
export const heroLegacy = (hero: HeroState): Legacy | null => {
  const def = sendaDef(hero);
  const st = sendaOf(hero);
  if (!def || !st || st.ch < def.chapters.length) return null;
  const title = legacyTitle(def.cls);
  return title ? { cls: def.cls, title } : null;
};

/**
 * The heroes of this campaign who walked their path to its end, by player id, with the path's marks (the bastion
 * keeps it as the state changes): what a fight reads to know them (an Unsleeping's line for the hero of her class).
 */
export const LEGACY_HEROES = new Map<string, { cls: HeroClass; flags: string[] }>();

/** Keeps LEGACY_HEROES as the campaign stands. */
export const noteLegacies = (players: readonly { id: string; hero: HeroState }[]): void => {
  LEGACY_HEROES.clear();
  for (const p of players) {
    const lg = heroLegacy(p.hero);
    if (lg) LEGACY_HEROES.set(p.id, { cls: lg.cls, flags: sendaOf(p.hero)?.flags ?? [] });
  }
};

/** A hero in a fight: it carries its legacy there (`lg`). */
export const battleLegacy = (hero: Pick<Hero, 'lg' | 'cls'>): Legacy | null => {
  if (!hero.lg) return null;
  const title = legacyTitle(hero.cls);
  return title ? { cls: hero.cls, title } : null;
};

/**
 * The name, as a chip: plain, or the legacy's plate. `far`: the emblem and the name only (the board's camera pulled
 * back; the title shows close up); `ghost`: a spectre's quieter tone.
 */
export const nameChip = (name: string, legacy: Legacy | null, o: { me?: boolean; ghost?: boolean; far?: boolean; test?: string } = {}): HTMLElement => {
  const cls = `${legacy ? '.lg' : ''}${o.me ? '.me' : ''}${o.ghost ? '.ghost' : ''}${o.far ? '.far' : ''}`;
  if (!legacy) return h(`span.nchip${cls}`, o.test ? { 'data-test': o.test } : {}, h('b.nchip-name', {}, name));
  return h(
    `span.nchip${cls}`,
    { 'data-test': o.test ?? 'legacy-chip', title: `${name}, ${legacy.title}` },
    h('span.nchip-sigil', { 'aria-hidden': 'true' }, h('i.nchip-ember'), h('span.nchip-ico', { html: SIGIL[legacy.cls] })),
    h('span.nchip-txt', {}, h('b.nchip-name', {}, name), h('span.nchip-title', {}, legacy.title)),
  );
};
